import { inject, Injectable } from '@angular/core'
import { FeathersService } from './feathers.service'
import { firstValueFrom, Observable } from 'rxjs'
import { first, map, shareReplay, switchMap, tap } from 'rxjs/operators'
import { DateRange, DateUtils } from './date-utils.service'
import { ToggleableModules } from '../features/config/toggleable-modules/ToggleableModules'
import { Config } from '../features/config/Config.model'
import { ConfigKeys } from '../features/config/ConfigKeys'
import { DateSegment } from '../models/DateTime/DateSegment'
import { format, parseISO } from 'date-fns'

export type OrganisationData = {
  name: string
  logo: string
  ico: string
  bank: string
  address: string
  accountNumber: string
  phone: string
}

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private feathers = inject(FeathersService)

  stream$: Observable<Config[]>
  private TAG: string = 'ConfigService'
  private defaultConfigs: Config[] = []

  constructor() {
    console.log(this.TAG, 'constructor')
    this.init()
    this.stream$.subscribe()
  }

  public getAllWithCaching(): Observable<Config[]> {
    console.log(this.TAG, 'getAllWithCaching')
    return this.stream$
  }

  public getByKey(key: ConfigKeys) {
    return this.getAllWithCaching().pipe(
      tap((configs) => console.log(`${this.TAG}#getByKey`, configs)),
      map((configs) => {
        const res = configs.filter((c) => c.name === key)
        if (Array.isArray(res) && res.length > 0) return res[0]
        else return null
      }),
    )
  }

  public async updateByKey(key: ConfigKeys, value: string) {
    return await this.getByKey(key)
      .pipe(
        first(),
        switchMap((config) => {
          config.value = value
          return this.update(config)
        }),
        first(),
      )
      .toPromise()
  }

  public getValue(name: ConfigKeys): Observable<any> {
    console.log(this.TAG, 'getValue')
    return this.stream$.pipe(
      tap((e) => console.log(e)),
      map((configs) => configs.filter((c) => c.name === name)),
      map((filteredConfig) => {
        if (filteredConfig && filteredConfig.length > 0) {
          return filteredConfig[0].value
        }
        return null
      }),
      tap((e) => console.log(e)),
    )
  }

  public getBoolean(name: ConfigKeys): Observable<boolean> {
    return this.getValue(name).pipe(map((val) => JSON.parse(val)))
  }

  private init() {
    console.log(this.TAG, 'init')

    this.initDefaultConfigs()

    this.stream$ = this.feathers.find(Config, {}).pipe(
      map((configs) => this.beforeDownHook(configs)),
      map((configs) => {
        const configsNotOnServer = this.defaultConfigs.filter(
          (defaultConfig) => !configs.some((c) => c.name === defaultConfig.name),
        )
        return configs.concat(configsNotOnServer)
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  private initDefaultConfigs() {
    Object.keys(ConfigKeys).forEach((key) => {
      const c = new Config(ConfigKeys[key])
      this.defaultConfigs.push(c)
    })
  }

  update(config: Config) {
    console.log(this.TAG, 'update', config)
    config = this.beforeUpHook(config)
    return this.feathers.upsert<Config>('configs', config)
  }

  private beforeDownHook(configs: Config[]): Config[] {
    configs.forEach((c) => {
      switch (c.name) {
        case ConfigKeys.ODKDY_JE_POZDE_OBJEDNANA_ASISTENCE_HODINY:
          c.value = DateUtils.minutesToHours(Number(c.value)).toString()
          break
        case ConfigKeys.ODKDY_JE_POZDE_ZRUSENA_JIZ_POTVRZENA_ASISTENCE_HODINY:
          c.value = DateUtils.minutesToHours(Number(c.value)).toString()
          break
        case ConfigKeys.ORDINACNI_HODINY_START_NO_TZ:
          c.value = DateUtils.militaryTimeToToday(c.value)
          break
        case ConfigKeys.ORDINACNI_HODINY_END_NO_TZ:
          c.value = DateUtils.militaryTimeToToday(c.value)
          break
        case ConfigKeys.TOGGLEABLE_MODULES:
          c.value = new ToggleableModules(JSON.parse(c.value))
          break
        case ConfigKeys.WORK_ATTENDANCE_BALANCE_FROM:
          c.value = c.value ? DateSegment.fromDate(parseISO(c.value)) : null
          break
      }
    })

    return configs
  }

  private beforeUpHook(c: Config): Config {
    switch (c.name) {
      case ConfigKeys.ODKDY_JE_POZDE_OBJEDNANA_ASISTENCE_HODINY:
        c.value = DateUtils.hoursToMinutes(Number(c.value)).toString()
        break
      case ConfigKeys.ODKDY_JE_POZDE_ZRUSENA_JIZ_POTVRZENA_ASISTENCE_HODINY:
        c.value = DateUtils.hoursToMinutes(Number(c.value)).toString()
        break
      case ConfigKeys.ORDINACNI_HODINY_START_NO_TZ:
        c.value = DateUtils.getMilitaryTime(new Date(c.value), false)
        break
      case ConfigKeys.ORDINACNI_HODINY_END_NO_TZ:
        c.value = DateUtils.getMilitaryTime(new Date(c.value), false)
        break
      case ConfigKeys.TOGGLEABLE_MODULES:
        console.log(`${this.TAG}#beforeUpHook active_modules`, c)
        // c.value = (c.value as ActiveModules).toJson()
        break
      case ConfigKeys.WORK_ATTENDANCE_BALANCE_FROM:
        c.value = c.value ? format(c.value.toDate(), 'yyyy-MM-dd') : ''
        break
    }

    return c
  }

  // Config values convenience functions

  async getWorkingHours(): Promise<DateRange> {
    return {
      from: new Date(await this.getValue(ConfigKeys.ORDINACNI_HODINY_START_NO_TZ).pipe(first()).toPromise()),
      to: new Date(await this.getValue(ConfigKeys.ORDINACNI_HODINY_END_NO_TZ).pipe(first()).toPromise()),
    }
  }
  async getLateOrderMinutesToNextMondayLimit() {
    return DateUtils.hoursToMinutes(
      Number(await this.getValue(ConfigKeys.ODKDY_JE_POZDE_OBJEDNANA_ASISTENCE_HODINY).pipe(first()).toPromise()),
    )
  }
  async getLateOrderHoursToNextMondayLimit() {
    return Number(await this.getValue(ConfigKeys.ODKDY_JE_POZDE_OBJEDNANA_ASISTENCE_HODINY).pipe(first()).toPromise())
  }
  async getLateOrderAssistanceLimitAppliesToCoordinators() {
    return await this.getBoolean(ConfigKeys.POZDE_OBJEDNANA_JESTLI_SE_VZTAHUJE_NA_KOORDINATORY)
      .pipe(first())
      .toPromise()
  }
  async getLateCanceledConfirmedAssistanceLimitMinutesToAssistance() {
    return DateUtils.minutesToHours(
      Number(
        await this.getValue(ConfigKeys.ODKDY_JE_POZDE_ZRUSENA_JIZ_POTVRZENA_ASISTENCE_HODINY).pipe(first()).toPromise(),
      ),
    )
  }
  async getLateCanceledConfirmedAssistanceLimitHoursToAssistance() {
    return Number(
      await this.getValue(ConfigKeys.ODKDY_JE_POZDE_ZRUSENA_JIZ_POTVRZENA_ASISTENCE_HODINY).pipe(first()).toPromise(),
    )
  }
  async getEditingPastAssistanceByCoordinatorDays() {
    return Number(
      await this.getValue(ConfigKeys.DOKDY_JE_MOZNA_EDITACE_PROBEHLYCH_ASISTENCI_DNY).pipe(first()).toPromise(),
    )
  }

  async getContactsLogoData() {
    return {
      logo: await firstValueFrom(this.getValue(ConfigKeys.ORGANIZACE_LOGO)),
      contact_information: await firstValueFrom(this.getValue(ConfigKeys.DEPRECATED_KONTAKTY)),
    }
  }

  async getOrganisationData(): Promise<OrganisationData> {
    return {
      name: await firstValueFrom(this.getValue(ConfigKeys.ORGANIZACE_NAZEV)),
      logo: await firstValueFrom(this.getValue(ConfigKeys.ORGANIZACE_LOGO)),
      ico: await firstValueFrom(this.getValue(ConfigKeys.ORGANIZACE_ICO)),
      bank: await firstValueFrom(this.getValue(ConfigKeys.ORGANIZACE_BANKA)),
      address: await firstValueFrom(this.getValue(ConfigKeys.ORGANIZACE_ADRESA_SIDLA)),
      accountNumber: await firstValueFrom(this.getValue(ConfigKeys.ORGANIZACE_CISLO_UCTU)),
      phone: await firstValueFrom(this.getValue(ConfigKeys.ORGANIZACE_TELEFON)),
    }
  }
}
